/**
 * API Client Configuration
 * Axios instance with interceptors for authentication and error handling
 */

import axios, { AxiosError } from 'axios';
import type { InternalAxiosRequestConfig } from 'axios';
import type { ApiResponse } from '@/types/api.types';
import { useAuthStore } from '@/store/auth.store';
import { getStorageAdapter } from './platform';

// Base API URL - configured via Vite proxy in development
const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || '/api';

// Create axios instance
export const apiClient = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: 30000, // 30 second timeout
  // Send the httpOnly refresh-token cookie ('rt') to the API on every request
  // (audit #17). The refresh token is no longer stored in client-side JS.
  withCredentials: true,
});

// Token storage keys
const ACCESS_TOKEN_KEY = 'access_token';
// Legacy key — the refresh token used to be persisted here before it was moved
// into an httpOnly cookie (audit #17). Kept only so clearTokens() can purge it
// from storage for sessions that pre-date the change.
const LEGACY_REFRESH_TOKEN_KEY = 'refresh_token';

// Token management — delegates to platform storage adapter.
// Only the short-lived access token is persisted client-side; the 7-day refresh
// token lives exclusively in an httpOnly cookie (audit #17).
export const tokenManager = {
  getAccessToken: (): string | null => {
    return getStorageAdapter().getItem(ACCESS_TOKEN_KEY);
  },

  setAccessToken: (token: string): void => {
    getStorageAdapter().setItem(ACCESS_TOKEN_KEY, token);
  },

  clearTokens: (): void => {
    getStorageAdapter().removeItem(ACCESS_TOKEN_KEY);
    // Purge any legacy client-stored refresh token from pre-cookie sessions.
    getStorageAdapter().removeItem(LEGACY_REFRESH_TOKEN_KEY);
  },

  hasTokens: (): boolean => {
    return !!getStorageAdapter().getItem(ACCESS_TOKEN_KEY);
  },
};

/**
 * Sanitize data to prevent JSON parsing errors
 * Handles control characters that break JSON.parse()
 */
const sanitizeForJSON = (data: unknown): unknown => {
  // Null, undefined, primitives
  if (data === null || data === undefined) {
    return data;
  }

  // Strings: This is where the issue occurs
  if (typeof data === 'string') {
    // Don't escape - Axios will handle serialization correctly
    // Just return as-is, Axios handles it properly
    return data;
  }

  // Arrays: recursively sanitize
  if (Array.isArray(data)) {
    return data.map(item => sanitizeForJSON(item));
  }

  // Objects: recursively sanitize
  if (typeof data === 'object') {
    const sanitized: Record<string, unknown> = {};
    for (const [key, value] of Object.entries(data)) {
      sanitized[key] = sanitizeForJSON(value);
    }
    return sanitized;
  }

  // Other types (numbers, booleans)
  return data;
};

// Request interceptor to add auth token and sanitize data
apiClient.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    // Add auth token
    const token = tokenManager.getAccessToken();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    // Sanitize request data to prevent JSON parsing errors
    // CRITICAL: Skip sanitization for FormData (file uploads)
    if (config.data && !(config.data instanceof FormData)) {
      config.data = sanitizeForJSON(config.data);
    }

    // For FormData, remove the default Content-Type header
    // to let axios set the correct multipart/form-data boundary
    if (config.data instanceof FormData) {
      delete config.headers['Content-Type'];
    }

    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor for error handling and token refresh
let isRefreshing = false;
let failedQueue: Array<{
  resolve: (value?: unknown) => void;
  reject: (reason?: unknown) => void;
}> = [];

const processQueue = (error: Error | null, token: string | null = null) => {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });

  failedQueue = [];
};

apiClient.interceptors.response.use(
  (response) => {
    return response;
  },
  async (error: AxiosError<ApiResponse>) => {
    const originalRequest = error.config;

    // Skip token refresh for auth endpoints that return 401 as normal flow (2FA, login)
    const authNoRefreshPaths = ['/auth/login', '/auth/verify-2fa', '/auth/resend-2fa'];
    const requestUrl = originalRequest?.url || '';
    const isAuthNoRefresh = authNoRefreshPaths.some(path => requestUrl.includes(path));

    if (isAuthNoRefresh && error.response?.status === 401) {
      // Extract the API error message and throw it directly
      const apiMessage = error.response?.data?.message || error.response?.data?.error || 'Authentication failed';
      return Promise.reject(new Error(apiMessage));
    }

    // If error is 401 and we haven't tried to refresh yet
    if (error.response?.status === 401 && originalRequest && !('_retry' in originalRequest)) {
      if (isRefreshing) {
        // Wait for the token refresh to complete
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        })
          .then((token) => {
            if (originalRequest.headers) {
              originalRequest.headers.Authorization = `Bearer ${token}`;
            }
            return apiClient(originalRequest);
          })
          .catch((err) => {
            return Promise.reject(err);
          });
      }

      (originalRequest as typeof originalRequest & { _retry?: boolean })._retry = true;
      isRefreshing = true;

      try {
        // Attempt to refresh the access token. The refresh token is carried by
        // the httpOnly 'rt' cookie (audit #17), so no token is sent in the body.
        const response = await axios.post<ApiResponse<{ accessToken: string }>>(
          `${API_BASE_URL}/auth/refresh`,
          {},
          { withCredentials: true }
        );

        if (response.data.data) {
          const { accessToken } = response.data.data;

          // Update the access token (refresh token stays in the httpOnly cookie)
          tokenManager.setAccessToken(accessToken);

          // Update authorization header
          if (originalRequest.headers) {
            originalRequest.headers.Authorization = `Bearer ${accessToken}`;
          }

          processQueue(null, accessToken);
          return apiClient(originalRequest);
        }
      } catch (refreshError) {
        processQueue(refreshError as Error, null);

        // Only logout on definitive auth rejection (401/403 from refresh endpoint).
        // Network errors, 502, 503 etc. mean the server is temporarily unreachable
        // (e.g. during a deploy) — keep tokens so the user isn't logged out.
        const refreshStatus = (refreshError as AxiosError)?.response?.status;
        const isAuthRejection = refreshStatus === 401 || refreshStatus === 403;

        if (isAuthRejection) {
          tokenManager.clearTokens();
          useAuthStore.getState().logout();
          window.location.href = '/login';
        }

        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }

    // Handle other errors
    if (error.response?.data) {
      // Return API error message
      return Promise.reject(new Error(error.response.data.message || error.response.data.error || 'An error occurred'));
    }

    // Network error or other errors
    return Promise.reject(error);
  }
);

// Export configured axios instance
export default apiClient;
